/* eslint-disable react/jsx-props-no-spreading */
import cx from 'classnames';
import { useMemo, type VoidFunctionComponent } from 'react';

import useAutoFocusInput from './hooks/useAutoFocusInput';
import useCommandPalette from './hooks/useCommandPalette';
import useDialogHide from './hooks/useDialogHide';
import LinkResult from './LinkResult';
import NoResults from './NoResults';
import Results from './Results';
import Input from './SearchInput';
import { groupResultsByType } from './utils';

import styles from './Palette.module.less';

type Props = {
  onHide: () => void;
  isOpen: boolean;
};

/**
 * The main Palette component for the command palette.
 * This component is responsible for rendering the input field
 * and the search results.
 */
const Palette: VoidFunctionComponent<Props> = ({ onHide, isOpen }) => {
  const mainRef = useDialogHide(onHide);
  const inputRef = useAutoFocusInput(isOpen);
  const {
    items,
    getInputProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    hasNoResults,
    isAboutToFetchResults,
    isFetchingNewSearchResults,
  } = useCommandPalette(onHide);

  //  This looks a bit weird, but remember that the search is debounced, however we also want to show a loading state
  //  if the user has not typed anything yet at all. Hence this check.
  const isLoading = isFetchingNewSearchResults || (isAboutToFetchResults && !isFetchingNewSearchResults);

  const groupedResults = useMemo(() => groupResultsByType(items), [items]);

  //  We render each section separately, but Downshift needs to know the cumulative index of all the items.
  let cumulativeIndex = 0;

  return (
    <div className={cx(styles.backdrop, 'animated quick fadeIn')}>
      <div
        ref={mainRef}
        role="dialog"
        aria-modal="true"
        className={cx(styles.main, 'animated quick fadeInUp')}
      >
        <Input
          {...getInputProps()}
          inputRef={inputRef}
          isLoading={isLoading}
        />
        <div className={styles.resultsContainer}>
          {hasNoResults ? <NoResults isLoading={isLoading} /> : (
            <>
              {(groupedResults.get('Pages') || [])?.length > 0 && (
                <Results {...getMenuProps()} sectionHeading="Pages">
                  {groupedResults.get('Pages')?.map((result) => (
                    <LinkResult
                      //  This needs to be above where we increment the cumulative index
                      //  Otherwise the highlighted index will be wrong
                      isHighlighted={highlightedIndex === cumulativeIndex}
                      key={result.label}
                      {...getItemProps({
                        item: result,
                        index: cumulativeIndex++,
                      })}
                      to={result.to}
                      label={result.label}
                    />
                  ))}
                </Results>
              )}
              {(groupedResults.get('Projects') || [])?.length > 0 && (
                <Results {...getMenuProps()} sectionHeading="Projects">
                  {groupedResults.get('Projects')?.map((result) => (
                    <LinkResult
                      //  This needs to be above where we increment the cumulative index
                      //  Otherwise the highlighted index will be wrong
                      isHighlighted={highlightedIndex === cumulativeIndex}
                      key={result.label}
                      {...getItemProps({
                        item: result,
                        index: cumulativeIndex++,
                      })}
                      to={result.to}
                      label={result.label}
                    />
                  ))}
                </Results>
              )}
              {(groupedResults.get('Release Groups') || [])?.length > 0 && (
                <Results {...getMenuProps()} sectionHeading="Release Groups">
                  {groupedResults.get('Release Groups')?.map((result) => (
                    <LinkResult
                      //  This needs to be above where we increment the cumulative index
                      //  Otherwise the highlighted index will be wrong
                      isHighlighted={highlightedIndex === cumulativeIndex}
                      key={result.label}
                      {...getItemProps({
                        item: result,
                        index: cumulativeIndex++,
                      })}
                      to={result.to}
                      label={result.label}
                    />
                  ))}
                </Results>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Palette;
